import { useState, useEffect, useCallback, useReducer, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import { useLocalStorageSync } from "./useLocalStorageSync";

const getInitialFlashCardData = () => {
  const exampleTopicId = uuidv4();
  const varCategoryId = uuidv4();
  const funcCategoryId = uuidv4();

  return {
    topics: [
      {
        id: exampleTopicId,
        name: "Example Topic: JavaScript Basics",
        categories: [
          { id: varCategoryId, name: "Variables" },
          { id: funcCategoryId, name: "Functions" },
        ],
      },
    ],
    cards: [
      {
        id: uuidv4(),
        topicId: exampleTopicId,
        categoryIds: [varCategoryId],
        frontContent:
          "What keyword is used to declare a variable that can be reassigned?",
        backContent: "`let`",
        difficulty: null, // Difficulty starts as null
      },
      {
        id: uuidv4(),
        topicId: exampleTopicId,
        categoryIds: [funcCategoryId],
        frontContent:
          "How do you define a function in JavaScript that returns the sum of two numbers?",
        backContent:
          "```javascript\nfunction sum(a, b) {\n  return a + b;\n}\n```",
        difficulty: null, // Difficulty starts as null
      },
    ],
  };
};

const initialData = getInitialFlashCardData(); // Simplified setup

const flashCardReducer = (state, action) => {
  switch (action.type) {
    case "SET_FLASHCARD_DATA":
      return {
        ...state,
        topics: action.payload.topics || [],
        cards: action.payload.cards || [],
      };
    case "ADD_TOPIC":
      const newTopic = {
        id: uuidv4(),
        name: action.payload.name,
        categories: [],
      };
      return { ...state, topics: [newTopic, ...state.topics] }; // Add to top for visibility
    case "UPDATE_TOPIC":
      return {
        ...state,
        topics: state.topics.map((topic) =>
          topic.id === action.payload.id
            ? { ...topic, ...action.payload.updates }
            : topic
        ),
      };
    case "DELETE_TOPIC":
      const remainingCardsAfterTopicDelete = state.cards.filter(
        (card) => card.topicId !== action.payload.topicId
      );
      return {
        ...state,
        topics: state.topics.filter(
          (topic) => topic.id !== action.payload.topicId
        ),
        cards: remainingCardsAfterTopicDelete,
      };

    case "ADD_CATEGORY_TO_TOPIC":
      return {
        ...state,
        topics: state.topics.map((topic) => {
          if (topic.id === action.payload.topicId) {
            // Check if category with the same name (case-insensitive) already exists
            if (
              topic.categories.find(
                (cat) =>
                  cat.name.toLowerCase() ===
                  action.payload.categoryName.toLowerCase()
              )
            ) {
              console.warn(
                `Category "${action.payload.categoryName}" already exists in this topic.`
              );
              return topic;
            }
            const newCategory = {
              id: uuidv4(),
              name: action.payload.categoryName.trim(),
            };
            return {
              ...topic,
              categories: [...topic.categories, newCategory].sort((a, b) =>
                a.name.localeCompare(b.name)
              ),
            }; // Keep categories sorted
          }
          return topic;
        }),
      };
    case "DELETE_CATEGORY_FROM_TOPIC":
      return {
        ...state,
        topics: state.topics.map((topic) => {
          if (topic.id === action.payload.topicId) {
            return {
              ...topic,
              categories: topic.categories.filter(
                (cat) => cat.id !== action.payload.categoryId
              ),
            };
          }
          return topic;
        }),
        cards: state.cards.map((card) => {
          if (
            card.topicId === action.payload.topicId &&
            card.categoryIds.includes(action.payload.categoryId)
          ) {
            return {
              ...card,
              categoryIds: card.categoryIds.filter(
                (catId) => catId !== action.payload.categoryId
              ),
            };
          }
          return card;
        }),
      };
    case "UPDATE_CATEGORY_IN_TOPIC":
      return {
        ...state,
        topics: state.topics.map((topic) => {
          if (topic.id === action.payload.topicId) {
            // Check for name collision before updating
            const otherCategories = topic.categories.filter(
              (cat) => cat.id !== action.payload.categoryId
            );
            if (
              otherCategories.find(
                (cat) =>
                  cat.name.toLowerCase() ===
                  action.payload.newName.toLowerCase()
              )
            ) {
              console.warn(
                `Another category with name "${action.payload.newName}" already exists.`
              );
              return topic;
            }
            return {
              ...topic,
              categories: topic.categories
                .map((cat) =>
                  cat.id === action.payload.categoryId
                    ? { ...cat, name: action.payload.newName.trim() }
                    : cat
                )
                .sort((a, b) => a.name.localeCompare(b.name)), // Keep sorted
            };
          }
          return topic;
        }),
      };

    case "ADD_CARD":
      // Ensure new cards have difficulty set to null by default
      const newCard = {
        id: uuidv4(),
        difficulty: null,
        ...action.payload.cardData,
      };
      return { ...state, cards: [newCard, ...state.cards] }; // Add new cards to the top
    case "UPDATE_CARD":
      return {
        ...state,
        cards: state.cards.map((card) =>
          card.id === action.payload.id
            ? { ...card, ...action.payload.updates }
            : card
        ),
      };
    case "DELETE_CARD":
      return {
        ...state,
        cards: state.cards.filter((card) => card.id !== action.payload.cardId),
      };

    default:
      return state;
  }
};

export function useFlashCards() {
  const { getItem, setItem } = useLocalStorageSync(["flashCardData"]);
  const [state, dispatch] = useReducer(flashCardReducer, {
    topics: [],
    cards: [],
  });
  const hasLoadedDataRef = useRef(false);

  useEffect(() => {
    if (hasLoadedDataRef.current) return;
    const storedData = getItem("flashCardData", null);

    let dataToLoad = initialData;
    if (storedData && storedData.topics && storedData.cards) {
      // Basic validation: ensure loaded data has expected structure
      if (Array.isArray(storedData.topics) && Array.isArray(storedData.cards)) {
        dataToLoad = storedData;
      } else {
        console.warn(
          "Stored flashcard data has incorrect structure, using initial data."
        );
      }
    }
    // Ensure all cards have a difficulty field, defaulting to null if missing
    dataToLoad.cards = dataToLoad.cards.map((card) => ({
      ...card,
      difficulty: card.difficulty === undefined ? null : card.difficulty,
    }));

    dispatch({ type: "SET_FLASHCARD_DATA", payload: dataToLoad });
    hasLoadedDataRef.current = true;
  }, [getItem]);

  useEffect(() => {
    if (!hasLoadedDataRef.current) return;
    if (
      state.topics.length > 0 ||
      state.cards.length > 0 ||
      localStorage.getItem("flashCardData") !== null
    ) {
      setItem("flashCardData", state);
    }
  }, [state, setItem]);

  const addTopic = useCallback(
    (name) => dispatch({ type: "ADD_TOPIC", payload: { name } }),
    []
  );
  const updateTopic = useCallback(
    (id, updates) =>
      dispatch({ type: "UPDATE_TOPIC", payload: { id, updates } }),
    []
  );
  const deleteTopic = useCallback(
    (topicId) => dispatch({ type: "DELETE_TOPIC", payload: { topicId } }),
    []
  );

  const addCategoryToTopic = useCallback(
    (topicId, categoryName) =>
      dispatch({
        type: "ADD_CATEGORY_TO_TOPIC",
        payload: { topicId, categoryName },
      }),
    []
  );
  const deleteCategoryFromTopic = useCallback(
    (topicId, categoryId) =>
      dispatch({
        type: "DELETE_CATEGORY_FROM_TOPIC",
        payload: { topicId, categoryId },
      }),
    []
  );
  const updateCategoryInTopic = useCallback(
    (topicId, categoryId, newName) =>
      dispatch({
        type: "UPDATE_CATEGORY_IN_TOPIC",
        payload: { topicId, categoryId, newName },
      }),
    []
  );

  const addCard = useCallback(
    (cardData) => dispatch({ type: "ADD_CARD", payload: { cardData } }),
    []
  );
  const updateCard = useCallback(
    (id, updates) =>
      dispatch({ type: "UPDATE_CARD", payload: { id, updates } }),
    []
  );
  const deleteCard = useCallback(
    (cardId) => dispatch({ type: "DELETE_CARD", payload: { cardId } }),
    []
  );

  return {
    topics: state.topics,
    cards: state.cards,
    addTopic,
    updateTopic,
    deleteTopic,
    addCategoryToTopic,
    deleteCategoryFromTopic,
    updateCategoryInTopic,
    addCard,
    updateCard,
    deleteCard,
  };
}
